import {
    EDITABLE_PROMPT_DEFAULT_VALUES,
    EMPTY_PROMPT_PHASE,
    PromptPhase,
    VoiceAgentEditablePromptFormData,
} from '../components/ConfigSteps.tsx';
import {
    PROMPT_APPROACH_OPTIONS,
    PROMPT_DEFAULT_APPROACH,
    PROMPT_DEFAULT_FORMALITY,
    PROMPT_FORMALITY_OPTIONS,
    PROMPT_GUARDRAILS,
    PromptApproach,
    PromptFormality,
    PromptGuardrail,
} from '../constants.ts';

// The Prompt step needs a role and at least one phase — for every agent, also one still on its legacy prompt
export const isEditablePromptValid = (prompt?: Partial<VoiceAgentEditablePromptFormData> | null): boolean =>
    Boolean(prompt?.role?.trim()) && (prompt?.phases?.length ?? 0) > 0;

// Whether the wizard's default display may still fill the prompt: nothing authored or imported yet
export const isEditablePromptEmpty = (prompt?: Partial<VoiceAgentEditablePromptFormData> | null): boolean =>
    !prompt?.role?.trim() && (prompt?.phases?.length ?? 0) === 0;

const toPhase = (raw: any): PromptPhase => ({
    goal: raw?.goal ?? '',
    entry: raw?.entry ?? '',
    procedure: raw?.procedure ?? '',
    response: raw?.response ?? '',
    nextStep: raw?.nextStep ?? raw?.next_step ?? '',
});

export const phasesFromTemplate = (phases: any[] | null | undefined): PromptPhase[] =>
    (phases || []).map((phase) => ({ ...EMPTY_PROMPT_PHASE, ...toPhase(phase) }));

const asGuardrails = (raw: unknown): PromptGuardrail[] =>
    (Array.isArray(raw) ? raw : [])
        .map((value) => String(value).toUpperCase())
        .filter(
            (value, index, all): value is PromptGuardrail =>
                (PROMPT_GUARDRAILS as readonly string[]).includes(value) && all.indexOf(value) === index
        );

const asFormality = (raw: unknown): PromptFormality => {
    const value = String(raw ?? '').toUpperCase();
    return (PROMPT_FORMALITY_OPTIONS as readonly string[]).includes(value)
        ? (value as PromptFormality)
        : PROMPT_DEFAULT_FORMALITY;
};

const asApproach = (raw: unknown): PromptApproach => {
    const value = String(raw ?? '').toUpperCase();
    return (PROMPT_APPROACH_OPTIONS as readonly string[]).includes(value)
        ? (value as PromptApproach)
        : PROMPT_DEFAULT_APPROACH;
};

// The agent's stored editable prompt (GraphQL VoiceAgentEditablePrompt) as form values; an agent without a row
// (none after the backfill, but defensively) gets the empty defaults
export const agentEditablePromptToFormValues = (prompt: any): VoiceAgentEditablePromptFormData => {
    if (!prompt) return { ...EDITABLE_PROMPT_DEFAULT_VALUES };
    return {
        role: prompt.role ?? '',
        guardrails: asGuardrails(prompt.guardrails),
        formality: asFormality(prompt.formality),
        approach: asApproach(prompt.approach),
        phases: phasesFromTemplate(prompt.phases),
        generalRemarks: prompt.generalRemarks ?? '',
    };
};

// An export file carries the backend's stored tokens (snake_case keys, lowercase enum values)
export const exportedEditablePromptToFormValues = (raw: Record<string, any>): VoiceAgentEditablePromptFormData => ({
    role: raw.role ?? '',
    guardrails: asGuardrails(raw.guardrails),
    formality: asFormality(raw.formality),
    approach: asApproach(raw.approach),
    phases: phasesFromTemplate(raw.phases),
    generalRemarks: raw.general_remarks ?? raw.generalRemarks ?? '',
});

// Exactly the six input fields the mutations accept, phases without anything the form may have attached
export const editablePromptToInput = (prompt: VoiceAgentEditablePromptFormData) => ({
    role: prompt.role,
    guardrails: prompt.guardrails,
    formality: prompt.formality,
    approach: prompt.approach,
    phases: prompt.phases.map(toPhase),
    generalRemarks: prompt.generalRemarks,
});
